import { useFormContext } from 'react-hook-form';
import { useCallback, useMemo } from 'react';
import { nonEmptyArray } from 'fp-ts';
import {
    DateService,
    DateTimeService,
    MonthService,
    useAppClockService,
    useDateTimeLocalizationInfo,
    useLocalizedMonthNames,
    useTimezone,
} from '@mews/framework';
import { Amount, ReservationLinkResult, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { WithAccountId } from '@mews-commander/types';
import { useGetReservationsForMonthlyBookableServicesSuspense } from '../../tables/PaymentPlans/hooks';
import { CreatePaymentPlanFormValues } from './schema';
import {
    CreatePaymentPlanFormField,
    MonthlyPaymentPlanAmount,
    PaymentPlanIntervalKind,
    ReservationForCreatePaymentPlan,
} from './types';
import { isReservationValid, getSortedMonthlyPaymentPlanAmounts } from './utils';

export const useGetNowDateTime = () => {
    const timezone = useTimezone();
    const appClockService = useAppClockService();
    return useCallback(
        () => DateTimeService.fromUtc(appClockService.getAppNowUtc(), timezone),
        [timezone, appClockService]
    );
};

export const useGetPaymentPlanDetailsIntervalParameters = (
    reservation: ReservationLinkResult
): { startUtc: string; endUtc: string } => {
    const paymentPlanStartDatesUtc = useGetReservationPaymentPlanStartDatesUtc(reservation);
    const { watch } = useFormContext<CreatePaymentPlanFormValues>();

    const startDateUtc = watch(CreatePaymentPlanFormField.StartDateUtc);
    const interval = watch(CreatePaymentPlanFormField.PaymentPlanIntervalKind);

    if (interval === PaymentPlanIntervalKind.Reservation || !paymentPlanStartDatesUtc.includes(startDateUtc)) {
        return {
            startUtc: reservation.reservationStartUtc,
            endUtc: reservation.reservationEndUtc,
        };
    }

    return {
        startUtc: startDateUtc,
        endUtc: reservation.reservationEndUtc,
    };
};

export const useGetReservationPaymentPlanStartDatesUtc = (reservation: ReservationLinkResult) => {
    const timezone = useTimezone();
    const getNowDateTime = useGetNowDateTime();

    return useMemo(() => {
        const reservationStartDateTime = DateTimeService.fromUtcString(reservation.reservationStartUtc, timezone);
        const reservationEndDateTime = DateTimeService.fromUtcString(reservation.reservationEndUtc, timezone);

        const startOfCurrentMonthDateTime = DateTimeService.startOfMonth(getNowDateTime());
        const startOfMonthBeforePlanDateTime = DateTimeService.max(
            startOfCurrentMonthDateTime,
            DateTimeService.startOfMonth(reservationStartDateTime)
        );
        const monthsDiff = DateTimeService.diffMonths(reservationEndDateTime, startOfMonthBeforePlanDateTime);

        if (monthsDiff < 2) {
            return [];
        }

        const availableMonthsRange = nonEmptyArray.range(1, monthsDiff - 1);

        return availableMonthsRange.map(
            (i) => DateTimeService.toUtc(DateTimeService.addMonths(startOfMonthBeforePlanDateTime, i)).value
        );
    }, [reservation, timezone, getNowDateTime]);
};

const useGetPaymentPlanCreateFormData = (reservation: ReservationLinkResult) => {
    const paymentPlanStartDatesUtc = useGetReservationPaymentPlanStartDatesUtc(reservation);

    const { watch } = useFormContext<CreatePaymentPlanFormValues>();
    const intervalKind = watch(CreatePaymentPlanFormField.PaymentPlanIntervalKind);
    const startDateUtc = watch(CreatePaymentPlanFormField.StartDateUtc);

    const { isReservationIntervalEnabled, isCustomIntervalEnabled } = useIsPaymentPlanIntervalKindEnabled(reservation);

    const useStartDateParam =
        (intervalKind === PaymentPlanIntervalKind.Custom && isCustomIntervalEnabled) || !isReservationIntervalEnabled;

    return useRepositorySuspenseQuery('paymentPlanCreateForm', 'get', {
        reservationId: reservation.reservationId,
        ...(useStartDateParam && {
            startDateUtc: paymentPlanStartDatesUtc.includes(startDateUtc) ? startDateUtc : paymentPlanStartDatesUtc[0],
        }),
    });
};

export const useGetPaymentPlanLocalizedDates = (reservation: ReservationLinkResult) => {
    const timezone = useTimezone();
    const localizationInfo = useDateTimeLocalizationInfo();
    const appClockService = useAppClockService();
    const { startUtc, endUtc } = useGetPaymentPlanDetailsIntervalParameters(reservation);

    const { data } = useGetPaymentPlanCreateFormData(reservation);
    const paymentRequestExpirationDateUtc = data.paymentRequestExpirationDateUtc ?? null;

    const planStartDateTime = DateTimeService.fromUtcString(startUtc, timezone);
    const paymentRequestExpirationDateTime = paymentRequestExpirationDateUtc
        ? DateTimeService.fromUtcString(paymentRequestExpirationDateUtc, timezone)
        : null;
    const nowDate = DateTimeService.toDate(DateTimeService.fromUtc(appClockService.getAppNowUtc(), timezone));
    const firstScheduledPaymentDateTime = DateTimeService.startOfMonth(DateTimeService.addMonths(planStartDateTime, 1));
    const firstScheduledPaymentDate = DateTimeService.toDate(firstScheduledPaymentDateTime);
    const planStartDate = DateTimeService.toDate(planStartDateTime);
    const planStartMonthEndDate = DateTimeService.toDate(DateTimeService.endOfMonth(planStartDateTime));
    const planEndDate = DateTimeService.toDate(DateTimeService.fromUtcString(endUtc, timezone));

    return {
        firstPaymentRequestExpirationDate: paymentRequestExpirationDateTime
            ? DateTimeService.toString(paymentRequestExpirationDateTime, localizationInfo)
            : null,
        firstPaymentRequestDate: DateService.toString(nowDate, localizationInfo),
        firstScheduledPaymentDate: DateService.toString(firstScheduledPaymentDate, localizationInfo),
        firstPaymentCoversStartDate: DateService.toString(planStartDate, localizationInfo),
        firstPaymentCoversEndDate: DateService.toString(planStartMonthEndDate, localizationInfo),
        planStartDate: DateService.toString(planStartDate, localizationInfo),
        planEndDate: DateService.toString(planEndDate, localizationInfo),
    };
};

export const useGetAmountsForPaymentPlanSuspense = (
    reservation: ReservationLinkResult
): { monthlyAmounts: MonthlyPaymentPlanAmount[]; averageMonthlyRate: Amount | null } => {
    const timezone = useTimezone();
    const monthNames = useLocalizedMonthNames();
    const { data } = useGetPaymentPlanCreateFormData(reservation);

    if (!data.paymentDueDatesAndAmounts || !data.averageMonthlyRate) {
        return { monthlyAmounts: [], averageMonthlyRate: null };
    }

    const monthlyAmounts = data.paymentDueDatesAndAmounts.map(({ dueDateUtc, amount }) => {
        if (!dueDateUtc || !amount) {
            return undefined;
        }

        const dateTime = DateTimeService.fromUtcString(dueDateUtc, timezone);
        const month = DateTimeService.toMonth(dateTime);
        const monthIndex = MonthService.getMonth(month);

        return {
            monthName: monthNames[monthIndex],
            amount,
            monthIndex: month.value,
        };
    });

    return {
        monthlyAmounts: getSortedMonthlyPaymentPlanAmounts(monthlyAmounts),
        averageMonthlyRate: data.averageMonthlyRate,
    };
};

export const useGetReservationsForCreatePaymentPlanSuspense = ({
    accountId,
}: WithAccountId): ReservationForCreatePaymentPlan[] => {
    const timezone = useTimezone();
    const getNowDateTime = useGetNowDateTime();

    const reservations = useGetReservationsForMonthlyBookableServicesSuspense({ accountId });

    return useMemo(() => {
        const nowDate = DateTimeService.toDate(getNowDateTime());

        return reservations
            .filter((reservation) => {
                const reservationEndDate = DateTimeService.toDate(
                    DateTimeService.fromUtcString(reservation.reservationEndUtc, timezone)
                );

                return DateService.isLess(nowDate, reservationEndDate);
            })
            .map((reservation) => ({
                isValid: isReservationValid(reservation, getNowDateTime, timezone),
                ...reservation,
            }));
    }, [timezone, getNowDateTime, reservations]);
};

export const useIsPaymentPlanIntervalKindEnabled = (reservation: ReservationLinkResult) => {
    const timezone = useTimezone();
    const getNowDateTime = useGetNowDateTime();
    const nowMonth = DateTimeService.toMonth(getNowDateTime());
    const reservationStartMonth = DateTimeService.toMonth(
        DateTimeService.fromUtcString(reservation.reservationStartUtc, timezone)
    );
    const isReservationIntervalEnabled = MonthService.isLessOrEqual(nowMonth, reservationStartMonth);

    const customReservationStartDates = useGetReservationPaymentPlanStartDatesUtc(reservation);
    const isCustomIntervalEnabled = customReservationStartDates.length > 0;

    return { isReservationIntervalEnabled, isCustomIntervalEnabled };
};
